import { createSlice } from "@reduxjs/toolkit";

import { ACTIVITY_TYPES } from "Constants/constants";
import { setDeep } from "Libs/objectAccess";
import { normalize } from "Libs/utils";

import { loadActivitySuccess } from "./thunks";
import { loadTaskActivities } from "./thunks/loadTaskActivities.thunk";

import type { Activity } from "Api/activity/types";

/**
 * Task activities, isolated from the shared `activity` slice on purpose.
 *
 * The main `activity` slice partitions by resource hierarchy (byProject /
 * byEnvironment / …) and carries one shared `loading`/filter state per scope —
 * fetching task activities through it would clobber the environment feed's
 * filters. Tasks are a sub-filter of the environment stream with no server-side
 * endpoint of their own (see `loadTaskActivities`), so they get a dedicated
 * slice: own loading/error, no cross-talk. Mirrors `cronActivities.slice`.
 *
 * Keyed only `[projectId][environmentId]` (not per task): one request returns
 * every task's activity, normalized by id. `selectTaskActivities` derives a
 * single task's list. Project ids are globally unique, so the org isn't needed
 * to key.
 */
interface TaskActivitiesState {
  loading: boolean;
  error?: string;
  data: {
    [projectId: string]: {
      [environmentId: string]: {
        [activityId: string]: Activity;
      };
    };
  };
}

const initialState: TaskActivitiesState = {
  loading: false,
  error: undefined,
  data: {}
};

const taskActivitiesSlice = createSlice({
  name: "taskActivities",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadTaskActivities.pending, state => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(loadTaskActivities.fulfilled, (state, { payload }) => {
        const { projectId, environmentId, activities } = payload;
        state.loading = false;
        setDeep(
          state,
          ["data", projectId, environmentId],
          normalize(activities || [], "id")
        );
      })
      .addCase(loadTaskActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Keep a loaded task list live: merge task activities arriving over the
      // websocket. Only into an environment bucket we already fetched, so we
      // never create a partial bucket for an environment the user never opened.
      .addCase(loadActivitySuccess.fulfilled, (state, { payload = [] }) => {
        payload.forEach(
          ({ projectDescriptionId, environmentDescriptionId, activity }) => {
            if (activity.type !== ACTIVITY_TYPES.ENVIRONMENT.TASK) return;

            const bucket =
              state.data?.[projectDescriptionId]?.[environmentDescriptionId];
            if (!bucket) return;

            setDeep(
              state,
              [
                "data",
                projectDescriptionId,
                environmentDescriptionId,
                activity.id
              ],
              activity
            );
          }
        );
      });
  }
});

export default taskActivitiesSlice.reducer;
